import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Vehicle,
  VehicleLocation,
  VehicleWithHistory,
} from "../../api/vehiclesApi";
import { LatLngLiteral } from "../../types/maps";

interface VehicleHistoryState {
  path: LatLngLiteral[];
  pauses: LatLngLiteral[];
}

interface VehiclesState {
  vehicles: VehicleWithHistory[];
  selectedVehicleId: number | null;
  selectedVehicleHistory?: VehicleHistoryState;
}

const initialState: VehiclesState = {
  vehicles: [],
  selectedVehicleId: null,
  selectedVehicleHistory: undefined,
};

export const vehiclesSlice = createSlice({
  name: "vehicles",
  initialState,
  reducers: {
    setVehicles: (state, action: PayloadAction<Vehicle[]>) => {
      state.vehicles = action.payload.map((vehicle) => ({
        ...vehicle,
        previousLocation: undefined,
      }));
    },
    selectVehicle: (state, action: PayloadAction<number | null>) => {
      state.selectedVehicleId = action.payload;
      if (!action.payload) {
        state.selectedVehicleHistory = undefined;
      }
    },
    updateVehiclesLocations: (
      state,
      action: PayloadAction<VehicleLocation[]>
    ) => {
      action.payload.forEach((location) => {
        const vehicleIndex = state.vehicles.findIndex(
          (v) => v.id === location.vehicleId
        );
        if (vehicleIndex !== -1) {
          const currentVehicle = state.vehicles[vehicleIndex];
          if (currentVehicle.lastLatitude && currentVehicle.lastLongitude) {
            state.vehicles[vehicleIndex] = {
              ...currentVehicle,
              previousLocation: {
                latitude: currentVehicle.lastLatitude,
                longitude: currentVehicle.lastLongitude,
                heading: location.heading,
                timestamp:
                  currentVehicle.lastLocationUpdate || new Date().toISOString(),
              },
              lastLatitude: location.latitude,
              lastLongitude: location.longitude,
              lastSpeed: location.speed,
              lastStatus: location.status,
              lastLocationUpdate: location.timestamp,
            };
          } else {
            state.vehicles[vehicleIndex] = {
              ...currentVehicle,
              lastLatitude: location.latitude,
              lastLongitude: location.longitude,
              lastSpeed: location.speed,
              lastStatus: location.status,
              lastLocationUpdate: location.timestamp,
            };
          }
        }
      });
    },
    setVehicleHistory: (state, action: PayloadAction<VehicleLocation[]>) => {
      if (!state.selectedVehicleId) return;

      const path: LatLngLiteral[] = [];
      const pauses: LatLngLiteral[] = [];

      for (let i = 0; i < action.payload.length; i++) {
        const current = action.payload[i];
        const next = action.payload[i + 1];

        const point = {
          lat: current.latitude,
          lng: current.longitude,
        };
        path.push(point);

        if (next) {
          const timeDiff =
            new Date(next.timestamp).getTime() -
            new Date(current.timestamp).getTime();
          if (timeDiff > 15 * 60 * 1000) {
            pauses.push(point);
          }
        }
      }

      state.selectedVehicleHistory = { path, pauses };
    },
    clearVehicleHistory: (state) => {
      state.selectedVehicleHistory = undefined;
    },
  },
});

export const {
  setVehicles,
  selectVehicle,
  updateVehiclesLocations,
  setVehicleHistory,
  clearVehicleHistory,
} = vehiclesSlice.actions;

export default vehiclesSlice.reducer;
